import {
  Check,
  ChevronRight,
  Circle,
  CircleDot,
  CircleSlash,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import type { RoomContextBullet } from "@/api/generated/RoomContextBullet"
import type { RoomDetail } from "@/api/generated/RoomDetail"
import { useAddBullet, useCompressRoom, useDeleteBullet, useUpdateBullet } from "@/api/queries/workplace"
import { useConfirm } from "@/components/app/confirm-dialog"
import { Markdown } from "@/components/app/markdown"
import { RelativeTime } from "@/components/app/relative-time"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { mergePlan, type PlanStep } from "./plan"

function StepIcon({ status }: { status: string }) {
  if (status === "done" || status === "completed") return <Check className="text-success size-3.5" />
  if (status === "in_progress" || status === "progress")
    return <CircleDot className="text-primary size-3.5" />
  if (status === "blocked" || status === "failed" || status === "skipped")
    return <CircleSlash className="text-warning size-3.5" />
  return <Circle className="text-muted-foreground size-3.5" />
}

function PlanSteps({ steps }: { steps: PlanStep[] }) {
  return (
    <ol className="space-y-1.5">
      {steps.map((s) => (
        <li key={s.id} className="flex items-start gap-2 text-[0.8125rem]">
          <span className="mt-0.5 shrink-0">
            <StepIcon status={s.status} />
          </span>
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                "break-words",
                (s.status === "done" || s.status === "completed") && "text-muted-foreground",
              )}
            >
              {s.label}
            </p>
            {s.notes && <p className="text-muted-foreground text-xs break-words">{s.notes}</p>}
          </div>
          <span className="text-muted-foreground shrink-0 font-mono text-[0.625rem]">{s.status}</span>
        </li>
      ))}
    </ol>
  )
}

function BulletItem({ roomId, bullet }: { roomId: number; bullet: RoomContextBullet }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const update = useUpdateBullet(roomId)
  const remove = useDeleteBullet(roomId)
  const [editing, setEditing] = React.useState(false)
  const [draft, setDraft] = React.useState(bullet.text)
  const isJson = bullet.text.trim().startsWith("{")
  return (
    <li className="group/bullet space-y-1.5 rounded-lg border px-3 py-2" data-testid="room-bullet">
      <div className="text-muted-foreground flex items-center gap-1.5 text-[0.6875rem]">
        <StatusBadge>{t(`workplace.room.context.category.${categoryKey(bullet.category)}`)}</StatusBadge>
        <span className="truncate">
          {bullet.author_worker_id != null
            ? t("workplace.room.context.byAgent")
            : t("workplace.room.context.byPerson")}
        </span>
        <span aria-hidden>·</span>
        <RelativeTime value={bullet.created_at} />
        <div className="ml-auto flex gap-0.5 opacity-0 transition-opacity group-focus-within/bullet:opacity-100 group-hover/bullet:opacity-100 max-md:opacity-100">
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={t("common.edit")}
            onClick={() => {
              setDraft(bullet.text)
              setEditing(true)
            }}
          >
            <Pencil />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            className="hover:text-destructive"
            aria-label={t("workplace.room.context.delete")}
            onClick={() =>
              void confirm({
                title: t("workplace.room.context.deleteTitle"),
                description: t("workplace.room.context.deleteDescription"),
                confirmLabel: t("workplace.room.context.delete"),
                destructive: true,
                onConfirm: () => remove.mutateAsync(bullet.id),
              })
            }
          >
            <Trash2 />
          </Button>
        </div>
      </div>
      {editing ? (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (!draft.trim()) return
            update.mutate({ id: bullet.id, text: draft.trim() }, { onSuccess: () => setEditing(false) })
          }}
        >
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={isJson ? 8 : 3}
            className={cn("text-[0.8125rem]", isJson && "font-mono text-xs")}
            aria-label={t("workplace.room.context.text")}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" size="sm" disabled={!draft.trim() || update.isPending}>
              {update.isPending && <Spinner />}
              {t("common.save")}
            </Button>
          </div>
        </form>
      ) : isJson ? (
        <pre className="text-muted-foreground max-h-40 overflow-auto font-mono text-[0.6875rem] break-words whitespace-pre-wrap">
          {bullet.text}
        </pre>
      ) : (
        <Markdown size="sm">{bullet.text}</Markdown>
      )}
    </li>
  )
}

const CATEGORIES = ["goal", "guardrail", "data_source", "discussion", "task_status", "other"] as const
function categoryKey(c: string): (typeof CATEGORIES)[number] {
  return (CATEGORIES as readonly string[]).includes(c) ? (c as (typeof CATEGORIES)[number]) : "other"
}

function SectionTitle({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="flex min-h-7 items-center justify-between gap-2">
      <h3 className="text-muted-foreground text-[0.6875rem] font-medium tracking-wide uppercase">
        {children}
      </h3>
      {action}
    </div>
  )
}

export function RoomContextPanel({ room }: { room: RoomDetail }) {
  const { t } = useTranslation()
  const plan = React.useMemo(() => mergePlan(room.room_context), [room.room_context])
  const add = useAddBullet(room.id)
  const compress = useCompressRoom(room.id)
  const [draft, setDraft] = React.useState("")
  const planBullets = room.room_context.filter((b) => b.category === "task_status" && !plan.notes.includes(b))
  const notes = [...plan.notes, ...plan.other]

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <SectionTitle>{t("workplace.room.context.plan")}</SectionTitle>
        {plan.steps.length === 0 && !plan.title ? (
          <p className="text-muted-foreground text-xs">{t("workplace.room.context.planEmpty")}</p>
        ) : (
          <div className="space-y-2 rounded-lg border p-3">
            {plan.title && <p className="text-sm font-medium">{plan.title}</p>}
            {plan.updatedReason && <p className="text-muted-foreground text-xs">{plan.updatedReason}</p>}
            <PlanSteps steps={plan.steps} />
          </div>
        )}
        {planBullets.length > 0 && (
          <Collapsible>
            <CollapsibleTrigger className="text-muted-foreground hover:text-foreground group/upd flex items-center gap-1 text-xs">
              <ChevronRight className="size-3.5 transition-transform group-data-[panel-open]/upd:rotate-90" />
              {t("workplace.room.context.planUpdates", { count: planBullets.length })}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-2 space-y-2">
                {planBullets.map((b) => (
                  <BulletItem key={b.id} roomId={room.id} bullet={b} />
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
      </section>

      <section className="space-y-2">
        <SectionTitle>{t("workplace.room.context.notes")}</SectionTitle>
        {notes.length > 0 && (
          <ul className="space-y-2">
            {notes.map((b) => (
              <BulletItem key={b.id} roomId={room.id} bullet={b} />
            ))}
          </ul>
        )}
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (!draft.trim()) return
            add.mutate({ text: draft.trim() }, { onSuccess: () => setDraft("") })
          }}
        >
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder={t("workplace.room.context.addPlaceholder")}
            aria-label={t("workplace.room.context.addPlaceholder")}
            className="min-h-16 text-[0.8125rem]"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-muted-foreground text-[0.6875rem]">{t("workplace.room.context.addHint")}</p>
            <Button type="submit" size="sm" variant="outline" disabled={!draft.trim() || add.isPending}>
              {add.isPending ? <Spinner /> : <Plus />}
              {t("workplace.room.context.add")}
            </Button>
          </div>
        </form>
      </section>

      <section className="space-y-2">
        <SectionTitle>{t("workplace.room.context.guardrails")}</SectionTitle>
        {room.guardrails.length === 0 ? (
          <p className="text-muted-foreground text-xs">{t("workplace.room.context.guardrailsEmpty")}</p>
        ) : (
          <ul className="space-y-1.5">
            {room.guardrails.map((g, i) => (
              <li key={i} className="flex items-start gap-2 text-[0.8125rem]">
                <ShieldCheck className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
                <span className="min-w-0 break-words">{g}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <SectionTitle
          action={
            <Button
              size="xs"
              variant="outline"
              onClick={() => compress.mutate()}
              disabled={compress.isPending}
            >
              {compress.isPending && <Spinner />}
              {t("workplace.room.memory.compress")}
            </Button>
          }
        >
          {t("workplace.room.memory.title")}
        </SectionTitle>
        {room.rolling_summary ? (
          <div className="space-y-1">
            <Collapsible>
              <CollapsibleTrigger className="text-muted-foreground hover:text-foreground group/sum flex items-center gap-1 text-xs">
                <ChevronRight className="size-3.5 transition-transform group-data-[panel-open]/sum:rotate-90" />
                {t("workplace.room.memory.summary")}
                {room.summary_updated_at && (
                  <>
                    <span aria-hidden>·</span>
                    <RelativeTime value={room.summary_updated_at} />
                  </>
                )}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="bg-muted/30 mt-2 max-h-72 overflow-y-auto rounded-lg border p-3">
                  <Markdown size="sm">{room.rolling_summary}</Markdown>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            {room.context_compression_enabled
              ? t("workplace.room.memory.noSummary", { count: room.context_max_messages })
              : t("workplace.room.memory.disabled")}
          </p>
        )}
      </section>
    </div>
  )
}
